import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "transition-transform transition-shadow duration-100 active:scale-95 active:shadow-inner inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-black shadow hover:bg-black/80 text-white",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-[#BB2020]",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        // Transparent toolbar style; consumer `className` can override text/hover colors.
        ghost:
          "bg-transparent text-foreground shadow-none hover:bg-accent hover:text-accent-foreground active:scale-100 active:shadow-none",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-lightGreen-900 text-white shadow-sm hover:bg-[#25942A]",
        grey: "text-black bg-[#f5f5f5] shadow hover:bg-[#f5f5f5]/80",
      },
      size: {
        default: "h-8 px-4 py-2 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode; // Add an icon prop
  fontWeight?: "normal" | "bold" | "medium"; // New prop for font weight
  color?: string; // New prop for custom text color (without 'text-' prefix)
  borderColor?: string; // New prop for custom border color (without 'border-' prefix)
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon,
      children,
      fontWeight = "normal",
      color,
      borderColor,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          fontWeight === "bold"
            ? "font-bold"
            : fontWeight === "medium"
              ? "font-medium"
              : "font-normal",
          color ? `text-${color}` : "", // Prepend 'text-' to the color value here
          borderColor ? `border-${borderColor}` : "", // Prepend 'border-' to the borderColor value here
          !asChild && "flex items-center justify-center",
          className,
        )}
        ref={ref}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {icon ? (
              <div className="flex w-6 items-center justify-center">{icon}</div>
            ) : null}
            <div>{children}</div>
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
